/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2025 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/
import styled from "styled-components";
import { useCurrentUser } from "actions/CurrentUserActions";

const FeatureFlagSectionContainer = styled.div`
  margin: 8px 0;
`;

const FeatureFlagSectionTitle = styled.h2`
  font-size: 16px;
  font-weight: 700;
`;

const useFeatureFlagDictionary = (): Record<string, boolean> => {
  const { currentUser } = useCurrentUser();

  const featureFlagDictionary: Record<string, boolean> = {};

  if (!currentUser) {
    return featureFlagDictionary;
  }

  for (const [key, value] of Object.entries(currentUser)) {
    const lowerCaseKey = key.toLowerCase();
    // Feature flag naming scheme isn't totally standardized - this is the best / most comprehensive way to check for now
    const isFeatureFlag =
      lowerCaseKey.includes("enabled") ||
      lowerCaseKey.includes("access") ||
      lowerCaseKey.includes("has") ||
      lowerCaseKey.includes("premium") ||
      lowerCaseKey.includes("use");

    const isBoolean = typeof value === "boolean";

    if (isFeatureFlag && isBoolean) {
      featureFlagDictionary[key] = value;
    }
  }

  return featureFlagDictionary;
};

const FeatureFlagTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 8px;

  th,
  td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #ddd;
  }

  th {
    background-color: #f2f2f2;
  }

  thead {
    background-color: #f2f2f2;
  }

  tr:nth-child(even) {
    background-color: #f9f9f9;
  }

  td.key {
    font-weight: bold;
  }

  td.true {
    color: green;
  }

  td.false {
    color: red;
  }
`;

const FeatureFlagTableComponent: React.FC = () => {
  const featureFlagDictionary = useFeatureFlagDictionary();

  return (
    <FeatureFlagTable>
      <thead>
        <tr>
          <th>Feature Flag</th>
          <th>Enabled</th>
        </tr>
      </thead>
      <tbody>
        {Object.entries(featureFlagDictionary)
          .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
          .map(([key, value]) => (
            <tr key={key}>
              <td className="key">{key}</td>
              <td className={value ? "true" : "false"}>{value.toString()}</td>
            </tr>
          ))}
      </tbody>
    </FeatureFlagTable>
  );
};

export const FeatureFlagDebugSection: React.FC = () => {
  const userHasAdminAccess = useCurrentUser().currentUser?.hasAdminAccess;
  const isStageOrEphemeralOrLocalEnv =
    process.env.PHONOS_GATEWAY_URL?.match(/stage/) ||
    process.env.PHONOS_GATEWAY_URL?.match(/ephemeral/) ||
    process.env.PHONOS_GATEWAY_URL?.match(/local/);
  const isProductionEnv = !isStageOrEphemeralOrLocalEnv;

  if (isProductionEnv && !userHasAdminAccess) {
    return null;
  }

  return (
    <FeatureFlagSectionContainer className="feature-flag-debug-section">
      <FeatureFlagSectionTitle>Feature Flags</FeatureFlagSectionTitle>
      <FeatureFlagTableComponent />
    </FeatureFlagSectionContainer>
  );
};
